import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Row, Col, DatePicker, Select, Button, Radio, Table, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { EChartsOption } from 'echarts';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import PageContainer from '@/components/PageContainer';
import ChartCard from '@/components/ChartCard';
import { usePublicData } from '@/api/public';
import { format2 } from '@/utils/charts';
import type { GridLoadRow, ClearingRow } from '@/types/api';

const { RangePicker } = DatePicker;

const AGG_OPTIONS = [
  { label: '15分钟', value: '15T' },
  { label: '1小时', value: '1h' },
  { label: '1天', value: 'D' },
];

const COLORS = {
  priceDA: '#0d9488',
  priceRT: '#52c41a',
  daPred: '#722ed1',
  ustPred: '#13c2c2',
  pvAvail: '#13c2c2',
  battery: '#722ed1',
  penalty: '#ff4d4f',
};

const ENERGY_COLORS = ['#0d9488', '#52c41a', '#faad14', '#f5222d', '#13c2c2', '#722ed1', '#eb2f96', '#2f54eb'];

/** 根据聚合粒度格式化时间标签 */
function formatTime(t: string, aggLevel: string): string {
  if (aggLevel === 'D') return t.slice(5, 10); // MM-DD
  return t.slice(11, 16); // HH:mm
}

/** 计算竞价空间 */
function computeBiddingSpace(row: GridLoadRow): number | null {
  const vals = [row.load_direct, row.gen_wind, row.gen_solar, row.load_tie_line, row.gen_nuclear, row.gen_local];
  if (vals.some((v) => v === null || v === undefined)) return null;
  return (row.load_direct ?? 0) - (row.gen_wind ?? 0) - (row.gen_solar ?? 0) - (row.load_tie_line ?? 0) - (row.gen_nuclear ?? 0) - (row.gen_local ?? 0);
}

/** 计算出清功率 MW */
function computeClearingPower(row: ClearingRow): number | null {
  if (row.clearing_power === null || row.clearing_power === undefined) return null;
  return row.clearing_power * 4;
}

/** ECharts 统一基线配置 */
function baseChartOption(): EChartsOption {
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      valueFormatter: (value: any) => (typeof value === 'number' ? format2(value) : value),
    },
    toolbox: { show: false },
    dataZoom: [{ type: 'slider', bottom: 8, height: 18 }],
    grid: { left: 60, right: 20, top: 56, bottom: 56 },
  };
}

const PublicPage: React.FC = () => {
  // 默认 D-2 / D-1 / D / D+1
  const today = dayjs();
  const dMinus2 = today.subtract(2, 'day');
  const dPlus1 = today.add(1, 'day');

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dMinus2, dPlus1]);
  const [aggLevel, setAggLevel] = useState<string>('1h');
  const [queryParams, setQueryParams] = useState({
    start_date: dMinus2.format('YYYY-MM-DD'),
    end_date: dPlus1.format('YYYY-MM-DD'),
    agg_level: '1h',
  });

  // 联动电价图表与竞价空间图表的 axisPointer
  useEffect(() => {
    echarts.connect('public-price-link');
    return () => {
      echarts.disconnect('public-price-link');
    };
  }, []);

  const { data, isLoading, error, refetch } = usePublicData(queryParams);

  const prices = data?.data.prices ?? [];
  const gridLoad = data?.data.grid_load ?? [];
  const clearing = data?.data.clearing ?? [];
  const unitParams = data?.data.unit_params ?? [];
  const maintenance = data?.data.maintenance ?? [];
  const constraints = data?.data.constraints ?? [];

  // 按 data_type 分组负荷数据
  const loadDA = useMemo(() => gridLoad.filter((r) => r.data_type === 'forecast'), [gridLoad]);
  const loadRT = useMemo(() => gridLoad.filter((r) => r.data_type === 'reality'), [gridLoad]);

  // ========== Row 1 Chart 1: 日前 vs 实时电价 ==========
  const priceChartOption: EChartsOption = useMemo(() => {
    if (prices.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [],
      };
    }
    return {
      ...baseChartOption(),
      legend: { data: ['日前电价', '实时均价'], top: 4, left: 'center' },
      xAxis: {
        type: 'category',
        data: prices.map((p) => formatTime(p.target_time, aggLevel)),
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af' },
      },
      yAxis: {
        type: 'value',
        name: '电价 (元/MWh)',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { color: '#9ca3af' },
      },
      series: [
        {
          name: '日前电价',
          type: 'line',
          data: prices.map((p) => p.price_da),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.priceDA, width: 2, type: 'dashed' },
          itemStyle: { color: COLORS.priceDA },
        },
        {
          name: '实时均价',
          type: 'line',
          data: prices.map((p) => p.price_rt_avg),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.priceRT, width: 2 },
          itemStyle: { color: COLORS.priceRT },
        },
      ],
    };
  }, [prices]);

  // ========== Row 1 Chart 2: 竞价空间趋势 ==========
  const biddingSpaceOption: EChartsOption = useMemo(() => {
    const daData = loadDA.map((r) => ({ time: formatTime(r.target_time, aggLevel), val: computeBiddingSpace(r) }));
    const rtData = loadRT.map((r) => ({ time: formatTime(r.target_time, aggLevel), val: computeBiddingSpace(r) }));
    if (daData.length === 0 && rtData.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [],
      };
    }
    return {
      ...baseChartOption(),
      legend: { data: ['日前预测', '实时实际'], top: 4, left: 'center' },
      xAxis: {
        type: 'category',
        data: daData.length > 0 ? daData.map((d) => d.time) : rtData.map((d) => d.time),
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af' },
      },
      yAxis: {
        type: 'value',
        name: '竞价空间 (MW)',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { color: '#9ca3af' },
      },
      series: [
        {
          name: '日前预测',
          type: 'line',
          data: daData.map((d) => d.val),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.daPred, width: 2, type: 'dashed' },
          itemStyle: { color: COLORS.daPred },
        },
        {
          name: '实时实际',
          type: 'line',
          data: rtData.map((d) => d.val),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.priceRT, width: 2 },
          itemStyle: { color: COLORS.priceRT },
        },
      ],
    };
  }, [loadDA, loadRT]);

  // ========== Row 2 Chart 1: 竞价空间-电价散点耦合 ==========
  const [scatterMode, setScatterMode] = useState<'DA' | 'RT' | 'both'>('RT');

  const scatterOption: EChartsOption = useMemo(() => {
    // 构建时间->价格的映射
    const priceMap = new Map<string, { da: number | null; rt: number | null }>();
    prices.forEach((p) => {
      priceMap.set(p.target_time, { da: p.price_da, rt: p.price_rt_avg });
    });

    // 构建时间->竞价空间的映射
    const bsMap = new Map<string, { da: number | null; rt: number | null }>();
    loadDA.forEach((r) => { bsMap.set(r.target_time, { da: computeBiddingSpace(r), rt: null }); });
    loadRT.forEach((r) => {
      const existing = bsMap.get(r.target_time);
      if (existing) existing.rt = computeBiddingSpace(r);
      else bsMap.set(r.target_time, { da: null, rt: computeBiddingSpace(r) });
    });

    const series: any[] = [];
    const dates = Array.from(new Set([...Array.from(priceMap.keys()), ...Array.from(bsMap.keys())])).sort();
    const dateGroups = new Map<string, Array<{ bs: number; price: number; mode: string }>>();

    dates.forEach((t) => {
      const p = priceMap.get(t);
      const bs = bsMap.get(t);
      const dateStr = t.slice(0, 10);
      if (!dateGroups.has(dateStr)) dateGroups.set(dateStr, []);
      const arr = dateGroups.get(dateStr)!;
      if ((scatterMode === 'DA' || scatterMode === 'both') && p?.da != null && bs?.da != null) {
        arr.push({ bs: bs.da, price: p.da, mode: '日前' });
      }
      if ((scatterMode === 'RT' || scatterMode === 'both') && p?.rt != null && bs?.rt != null) {
        arr.push({ bs: bs.rt, price: p.rt, mode: '实时' });
      }
    });

    const uniqueDates = Array.from(dateGroups.keys()).sort();
    uniqueDates.forEach((d, i) => {
      const color = ENERGY_COLORS[i % ENERGY_COLORS.length];
      const items = dateGroups.get(d)!;
      const daItems = items.filter((it) => it.mode === '日前');
      const rtItems = items.filter((it) => it.mode === '实时');
      if (daItems.length > 0) {
        series.push({
          name: `${d} 日前`,
          type: 'scatter',
          data: daItems.map((it) => [it.bs, it.price]),
          itemStyle: { color, opacity: 0.7 },
          symbol: 'emptyCircle',
        });
      }
      if (rtItems.length > 0) {
        series.push({
          name: `${d} 实时`,
          type: 'scatter',
          data: rtItems.map((it) => [it.bs, it.price]),
          itemStyle: { color, opacity: 0.9 },
          symbol: 'circle',
        });
      }
    });

    if (series.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'value', name: '竞价空间 (MW)' },
        yAxis: { type: 'value', name: '电价 (元/MWh)' },
        series: [],
      };
    }

    return {
      ...baseChartOption(),
      legend: { type: 'scroll', top: 4, left: 'center' },
      xAxis: { type: 'value', name: '竞价空间 (MW)', splitLine: { lineStyle: { color: '#f3f4f6' } } },
      yAxis: { type: 'value', name: '电价 (元/MWh)', splitLine: { lineStyle: { color: '#f3f4f6' } } },
      series,
    };
  }, [prices, loadDA, loadRT, scatterMode]);

  // ========== Row 2 Chart 2: 新能源出力态势 ==========
  const [reMode, setReMode] = useState<'DA' | 'RT' | 'both'>('both');

  const reGenOption: EChartsOption = useMemo(() => {
    const daWind = loadDA.map((r) => r.gen_wind);
    const daSolar = loadDA.map((r) => r.gen_solar);
    const rtWind = loadRT.map((r) => r.gen_wind);
    const rtSolar = loadRT.map((r) => r.gen_solar);
    const xData = loadDA.length > 0 ? loadDA.map((r) => formatTime(r.target_time, aggLevel)) : loadRT.map((r) => formatTime(r.target_time, aggLevel));

    if (xData.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [],
      };
    }

    const series: any[] = [];
    if (reMode === 'DA' || reMode === 'both') {
      series.push({
        name: '日前风', type: 'line', data: daWind, smooth: true, symbol: 'none',
        lineStyle: { color: COLORS.daPred, width: 2, type: 'dashed' },
        itemStyle: { color: COLORS.daPred },
      });
      series.push({
        name: '日前光', type: 'line', data: daSolar, smooth: true, symbol: 'none',
        lineStyle: { color: COLORS.ustPred, width: 2, type: 'dashed' },
        itemStyle: { color: COLORS.ustPred },
      });
    }
    if (reMode === 'RT' || reMode === 'both') {
      series.push({
        name: '实时风', type: 'line', data: rtWind, smooth: true, symbol: 'none',
        lineStyle: { color: COLORS.daPred, width: 2 },
        itemStyle: { color: COLORS.daPred },
      });
      series.push({
        name: '实时光', type: 'line', data: rtSolar, smooth: true, symbol: 'none',
        lineStyle: { color: COLORS.ustPred, width: 2 },
        itemStyle: { color: COLORS.ustPred },
      });
    }

    return {
      ...baseChartOption(),
      legend: { data: series.map((s) => s.name), top: 4, left: 'center' },
      xAxis: {
        type: 'category', data: xData,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af' },
      },
      yAxis: {
        type: 'value', name: '出力 (MW)',
        axisLine: { show: false }, axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { color: '#9ca3af' },
      },
      series,
    };
  }, [loadDA, loadRT, reMode]);

  // ========== Row 3 Chart 1: 类型机组出清功率 ==========
  const [clearingMode, setClearingMode] = useState<'DA' | 'RT' | 'both'>('DA');

  const clearingOption: EChartsOption = useMemo(() => {
    const daClearing = clearing.filter((c) => c.market_stage === 'DA');
    const rtClearing = clearing.filter((c) => c.market_stage === 'RT');

    // 按 unit_type 分组
    const daByType = new Map<string, Array<{ time: string; power: number | null }>>();
    const rtByType = new Map<string, Array<{ time: string; power: number | null }>>();

    daClearing.forEach((c) => {
      if (!daByType.has(c.unit_type)) daByType.set(c.unit_type, []);
      daByType.get(c.unit_type)!.push({ time: c.target_time, power: computeClearingPower(c) });
    });
    rtClearing.forEach((c) => {
      if (!rtByType.has(c.unit_type)) rtByType.set(c.unit_type, []);
      rtByType.get(c.unit_type)!.push({ time: c.target_time, power: computeClearingPower(c) });
    });

    const allTimes = Array.from(new Set(clearing.map((c) => c.target_time))).sort();
    const xData = allTimes.map((t) => formatTime(t, aggLevel));

    const series: any[] = [];
    let colorIdx = 0;

    if (clearingMode === 'DA' || clearingMode === 'both') {
      daByType.forEach((items, unitType) => {
        const timeMap = new Map(items.map((i) => [i.time, i.power]));
        const data = allTimes.map((t) => timeMap.get(t) ?? null);
        series.push({
          name: `${unitType}(DA)`,
          type: 'line',
          data,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: ENERGY_COLORS[colorIdx % ENERGY_COLORS.length], width: 2, type: 'dashed' },
          itemStyle: { color: ENERGY_COLORS[colorIdx % ENERGY_COLORS.length] },
        });
        colorIdx++;
      });
    }
    if (clearingMode === 'RT' || clearingMode === 'both') {
      rtByType.forEach((items, unitType) => {
        const timeMap = new Map(items.map((i) => [i.time, i.power]));
        const data = allTimes.map((t) => timeMap.get(t) ?? null);
        series.push({
          name: `${unitType}(RT)`,
          type: 'line',
          data,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: ENERGY_COLORS[colorIdx % ENERGY_COLORS.length], width: 2 },
          itemStyle: { color: ENERGY_COLORS[colorIdx % ENERGY_COLORS.length] },
        });
        colorIdx++;
      });
    }

    if (series.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [],
      };
    }

    return {
      ...baseChartOption(),
      legend: { type: 'scroll', top: 4, left: 'center' },
      xAxis: {
        type: 'category', data: xData,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af' },
      },
      yAxis: {
        type: 'value', name: '功率 (MW)',
        axisLine: { show: false }, axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { color: '#9ca3af' },
      },
      series,
    };
  }, [clearing, clearingMode]);

  // ========== Row 3 Chart 2: 火电容量 (必停/必开/检修) ==========
  const unitStatusOption: EChartsOption = useMemo(() => {
    if (unitParams.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [],
      };
    }

    // 构建 unit_params 查找表
    const paramMap = new Map<string, number>();
    unitParams.forEach((u) => {
      const key = `${u.plant_name}|${u.unit_id}`;
      paramMap.set(key, u.rated_capacity ?? 0);
    });

    // 生成时间序列索引 (按 agg_level 粒度)
    const start = dayjs(queryParams.start_date);
    const end = dayjs(queryParams.end_date);
    const timeLabels: string[] = [];
    const timePoints: dayjs.Dayjs[] = [];
    let cur = start.startOf('day');
    while (cur.isBefore(end.endOf('day'))) {
      timeLabels.push(cur.format('MM-DD HH:mm'));
      timePoints.push(cur.clone());
      if (aggLevel === 'D') cur = cur.add(1, 'day');
      else if (aggLevel === '1H') cur = cur.add(1, 'hour');
      else cur = cur.add(15, 'minute');
    }

    const jxData = new Array(timePoints.length).fill(0);
    const mustRunData = new Array(timePoints.length).fill(0);
    const mustStopData = new Array(timePoints.length).fill(0);

    // 先计算约束，并记录每个时间点有哪些机组被约束覆盖（约束优先于检修）
    const constraintCover = new Set<string>();
    constraints.forEach((c) => {
      const cap = paramMap.get(`${c.plant_name}|${c.unit_id}`) ?? 0;
      const s = dayjs(c.constraint_start);
      const e = dayjs(c.constraint_end);
      const target = c.constraint_type === '开机' ? mustRunData : c.constraint_type === '停机' ? mustStopData : null;
      if (!target) return;
      timePoints.forEach((tp, idx) => {
        if (!tp.isBefore(s) && tp.isBefore(e.clone().add(1, 'minute'))) {
          target[idx] += cap;
          constraintCover.add(`${c.plant_name}|${c.unit_id}|${idx}`);
        }
      });
    });

    // 计算检修：如果该机组该时间点已有约束覆盖，则跳过（避免重复计算）
    maintenance.forEach((m) => {
      const cap = paramMap.get(`${m.plant_name}|${m.unit_id}`) ?? 0;
      const s = dayjs(m.actual_start);
      const e = dayjs(m.actual_end);
      timePoints.forEach((tp, idx) => {
        if (!tp.isBefore(s) && tp.isBefore(e.clone().add(1, 'minute'))) {
          if (!constraintCover.has(`${m.plant_name}|${m.unit_id}|${idx}`)) {
            jxData[idx] += cap;
          }
        }
      });
    });

    return {
      ...baseChartOption(),
      legend: { data: ['检修', '必开', '必停'], top: 4, left: 'center' },
      xAxis: {
        type: 'category', data: timeLabels,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af', rotate: 45 },
      },
      yAxis: {
        type: 'value', name: '容量 (MW)',
        axisLine: { show: false }, axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { color: '#9ca3af' },
      },
      series: [
        { name: '检修', type: 'bar', stack: 'total', data: jxData, itemStyle: { color: COLORS.pvAvail } },
        { name: '必开', type: 'bar', stack: 'total', data: mustRunData, itemStyle: { color: COLORS.battery } },
        { name: '必停', type: 'bar', stack: 'total', data: mustStopData, itemStyle: { color: COLORS.penalty } },
      ],
    };
  }, [unitParams, maintenance, constraints, queryParams.start_date, queryParams.end_date, aggLevel]);

  // ========== 查询按钮 ==========
  const handleQuery = useCallback(() => {
    setQueryParams({
      start_date: dateRange[0].format('YYYY-MM-DD'),
      end_date: dateRange[1].format('YYYY-MM-DD'),
      agg_level: aggLevel,
    });
  }, [dateRange, aggLevel]);

  // ========== 表格列定义 ==========
  const clearingColumns = [
    { title: '时间', dataIndex: 'target_time', key: 'target_time' },
    { title: '市场阶段', dataIndex: 'market_stage', key: 'market_stage' },
    { title: '机组类型', dataIndex: 'unit_type', key: 'unit_type' },
    { title: '出清功率', dataIndex: 'clearing_power', key: 'clearing_power', render: (v: number | null) => v != null ? format2(v) : '-' },
  ];

  const unitParamsColumns = [
    { title: '电厂', dataIndex: 'plant_name', key: 'plant_name' },
    { title: '机组', dataIndex: 'unit_id', key: 'unit_id' },
    { title: '额定容量(MW)', dataIndex: 'rated_capacity', key: 'rated_capacity', render: (v: number | null) => v != null ? format2(v) : '-' },
  ];

  const maintenanceColumns = [
    { title: '电厂', dataIndex: 'plant_name', key: 'plant_name' },
    { title: '机组', dataIndex: 'unit_id', key: 'unit_id' },
    { title: '开始时间', dataIndex: 'actual_start', key: 'actual_start' },
    { title: '结束时间', dataIndex: 'actual_end', key: 'actual_end' },
  ];

  const constraintColumns = [
    { title: '电厂', dataIndex: 'plant_name', key: 'plant_name' },
    { title: '机组', dataIndex: 'unit_id', key: 'unit_id' },
    { title: '约束类型', dataIndex: 'constraint_type', key: 'constraint_type' },
    { title: '开始时间', dataIndex: 'constraint_start', key: 'constraint_start' },
    { title: '结束时间', dataIndex: 'constraint_end', key: 'constraint_end' },
  ];

  return (
    <PageContainer noCard>
      {/* 控制栏 */}
      <div style={{ paddingBottom: 16, marginBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={10}>
            <RangePicker
              value={dateRange}
              onChange={(vals) => {
                if (vals && vals[0] && vals[1]) setDateRange([vals[0], vals[1]]);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              value={aggLevel}
              onChange={setAggLevel}
              options={AGG_OPTIONS}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={4} md={4}>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} style={{ width: '100%' }}>
              查询
            </Button>
          </Col>
        </Row>
      </div>

      {/* Row 1: 电价 + 竞价空间 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <ChartCard title="日前 vs 实时电价对比" option={priceChartOption} loading={isLoading} error={error ?? null} onRetry={() => refetch()} height={380} echartsGroup="public-price-link" />
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="竞价空间趋势" option={biddingSpaceOption} loading={isLoading} error={error ?? null} onRetry={() => refetch()} height={380} echartsGroup="public-price-link" />
        </Col>
      </Row>

      {/* Row 2: 散点 + 新能源 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <ChartCard
            title="竞价空间-电价散点耦合"
            extra={
              <Radio.Group value={scatterMode} onChange={(e) => setScatterMode(e.target.value)} size="small">
                <Radio.Button value="DA">日前</Radio.Button>
                <Radio.Button value="RT">实时</Radio.Button>
                <Radio.Button value="both">双模式</Radio.Button>
              </Radio.Group>
            }
            option={scatterOption}
            loading={isLoading}
            error={error ?? null}
            onRetry={() => refetch()}
            height={340}
          />
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard
            title="新能源出力态势"
            extra={
              <Radio.Group value={reMode} onChange={(e) => setReMode(e.target.value)} size="small">
                <Radio.Button value="DA">日前</Radio.Button>
                <Radio.Button value="RT">实时</Radio.Button>
                <Radio.Button value="both">双模式</Radio.Button>
              </Radio.Group>
            }
            option={reGenOption}
            loading={isLoading}
            error={error ?? null}
            onRetry={() => refetch()}
            height={340}
          />
        </Col>
      </Row>

      {/* Row 3: 出清功率 + 火电容量 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        <Col xs={24} lg={12}>
          <ChartCard
            title="类型机组出清功率"
            extra={
              <Radio.Group value={clearingMode} onChange={(e) => setClearingMode(e.target.value)} size="small">
                <Radio.Button value="DA">日前</Radio.Button>
                <Radio.Button value="RT">实时</Radio.Button>
                <Radio.Button value="both">双模式</Radio.Button>
              </Radio.Group>
            }
            option={clearingOption}
            loading={isLoading}
            error={error ?? null}
            onRetry={() => refetch()}
            height={340}
          />
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="火电容量 (必停/必开/检修)" option={unitStatusOption} loading={isLoading} error={error ?? null} onRetry={() => refetch()} height={340} />
        </Col>
      </Row>

      {/* 数据表格 */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24 }}>
        <Tabs
          defaultActiveKey="clearing"
          items={[
            {
              key: 'clearing',
              label: '出清数据',
              children: (
                <Table dataSource={clearing} columns={clearingColumns} rowKey={(_r, i) => `c-${i}`} pagination={{ pageSize: 48 }} size="small" scroll={{ x: 'max-content' }} />
              ),
            },
            {
              key: 'unitParams',
              label: '机组参数',
              children: (
                <Table dataSource={unitParams} columns={unitParamsColumns} rowKey={(_r, i) => `u-${i}`} pagination={{ pageSize: 20 }} size="small" />
              ),
            },
            {
              key: 'maintenance',
              label: '检修计划',
              children: (
                <Table dataSource={maintenance} columns={maintenanceColumns} rowKey={(_r, i) => `m-${i}`} pagination={{ pageSize: 20 }} size="small" />
              ),
            },
            {
              key: 'constraints',
              label: '约束数据',
              children: (
                <Table dataSource={constraints} columns={constraintColumns} rowKey={(_r, i) => `cn-${i}`} pagination={{ pageSize: 20 }} size="small" />
              ),
            },
          ]}
        />
      </div>
    </PageContainer>
  );
};

export default PublicPage;
